import { useEffect, useRef } from "react";
import { PUBLISHER_ID, isAdSenseEnabled } from "../lib/adsense.js";
import { useConsent } from "./CookieConsent.jsx";

/* ============================================================
   AdSlot
   ============================================================
   Drop into any page where you want an ad. Renders nothing 
   until the user has consented to cookies/tracking AND 
   AdSense has been configured.
   
   Usage:
     <AdSlot slot="XXXXXXXX" />
     <AdSlot slot="XXXXXXXX" format="horizontal" />
   ============================================================ */

export default function AdSlot({
  slot,
  format = "auto",
  className = "",
  responsive = true,
}) {
  const insRef = useRef(null);
  const pushedRef = useRef(false);
  const { hasConsented } = useConsent();

  useEffect(() => {
    if (!isAdSenseEnabled() || !hasConsented) return;
    if (pushedRef.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch (e) {
      // AdSense errors are common (ad blocker, script not loaded yet)
      // Fail silently - we don't want broken ads to crash the page
      if (import.meta.env.DEV) {
        console.warn("AdSense push failed:", e?.message);
      }
    }
  }, [hasConsented]);

  // Dev placeholder so we can see where ads would appear
  if (!isAdSenseEnabled()) {
    if (import.meta.env.DEV) {
      return (
        <div
          className={`my-10 border-2 border-dashed border-border rounded-2xl p-8 text-center text-ink-soft text-sm ${className}`}
        >
          <div className="font-mono text-xs uppercase tracking-wider mb-1.5 opacity-70">
            AdSlot · {format}
          </div>
          <div className="text-xs opacity-60">
            Aparece em produção quando AdSense for aprovado e configurado
          </div>
        </div>
      );
    }
    return null;
  }

  // Consent not yet given — render nothing (avoids tracking before consent)
  if (!hasConsented) return null;

  return (
    <div className={`my-10 ${className}`}>
      <div className="text-[10px] uppercase tracking-wider text-ink-soft/60 mb-2 text-center font-mono">
        Publicidade
      </div>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? "true" : "false"}
      />
    </div>
  );
}
